import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Auto-advancing filmstrip for the home Edits cover.
 *
 * The element IS the scroll-snap scroller. Every few seconds it eases to
 * the next cover, wrapping back to the first after the last. The pace is
 * deliberately unhurried — a shop window turning, not a carousel selling.
 *
 * The visitor always wins: the first real interaction (touch, wheel,
 * pointer down, keyboard focus into a cover) retires the auto-advance for
 * good. It also never runs when reduced motion is requested, when the
 * strip is off-screen or hidden (the editorial desktop layout swaps it out), or when
 * the tab is in the background.
 */
class SoodamEditsFilmstrip extends Component {
  #timer = 0;
  #retired = false;
  #inView = false;
  #settleTimer = 0;
  /** True while a scroll we initiated is still settling. */
  #selfScrolling = false;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #interval = 4500;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion() || this.hasAttribute('data-no-autoadvance') || this.children.length < 2) {
      this.#retired = true;
      return;
    }

    this.addEventListener('pointerdown', this.#retire);
    this.addEventListener('wheel', this.#retire, { passive: true });
    this.addEventListener('touchstart', this.#retire, { passive: true });
    this.addEventListener('focusin', this.#retire);
    this.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#inView ? this.#schedule() : this.#stop();
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #schedule() {
    if (this.#retired || this.#timer || !this.#inView || document.hidden) return;
    this.#timer = window.setInterval(() => this.#advance(), this.#interval);
  }

  #stop() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = 0;
    }
  }

  #retire = () => {
    this.#retired = true;
    this.#stop();
  };

  #onVisibility = () => {
    document.hidden ? this.#stop() : this.#schedule();
  };

  /** A scroll we didn't start means the visitor is browsing — step aside. */
  #onScroll = () => {
    if (this.#selfScrolling) {
      window.clearTimeout(this.#settleTimer);
      this.#settleTimer = window.setTimeout(() => {
        this.#selfScrolling = false;
      }, 150);
      return;
    }
    this.#retire();
  };

  #advance() {
    if (this.#retired || document.hidden) return;

    const slides = Array.from(this.children).filter(
      (child) => child instanceof HTMLElement
    );
    if (slides.length < 2) return;

    const pad = parseFloat(getComputedStyle(this).scrollPaddingInlineStart) || 0;
    const origin = this.getBoundingClientRect().left;
    const maxScroll = this.scrollWidth - this.clientWidth;
    const targets = slides.map((slide) =>
      Math.min(
        slide.getBoundingClientRect().left - origin + this.scrollLeft - pad,
        maxScroll
      )
    );
    const next = targets.find((left) => left > this.scrollLeft + 1) ?? targets[0];
    if (next === undefined) return;

    this.#selfScrolling = true;
    this.scrollTo({ left: next, behavior: 'smooth' });
  }
}

if (!customElements.get('soodam-edits-filmstrip')) {
  customElements.define('soodam-edits-filmstrip', SoodamEditsFilmstrip);
}
